import { Link, useLocation, useNavigate } from "react-router-dom"
import Container from "../Container"
import * as S from "./styled"

import { ReactComponent as BurguerIcon } from "../../assets/icons/burguer.svg"
import { LifeBuoy, LogOut, Ticket, X } from "lucide-react"

import getStore from "../../store"
import { clearSession } from "../../utils/tb/session"
import { useEffect, useState } from "react"
import { TOrganizer } from "../../utils/@types/data/organizer"

type Props = {
  customData?: TOrganizer
  hideLogo?: boolean
}

type TNavItem = {
  key: string
  label: string
  to: string
  active: boolean
  icon: JSX.Element
}

const Header = ({ customData, hideLogo }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()

  const { event, user } = getStore()
  const isOnMyTickets = location.pathname.startsWith("/mytickets")
  const isOnSupport = location.pathname.startsWith("/suporte")

  const [menuOpened, setMenuOpened] = useState(false)

  const navState = {
    logoWebstoreUrl: event?.logoWebstoreUrl ?? customData?.webstoreLogoUrl,
  }

  const goTo = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    to: string,
  ) => {
    e.preventDefault()
    setMenuOpened(false)
    navigate(to, { state: navState })
  }

  const handleLogout = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault()
    setMenuOpened(false)
    clearSession()
    navigate("/", { replace: true, state: navState })
  }

  // Sem login, "Meus ingressos" leva ao login por código. Logado, some na
  // escolha de evento (como antes).
  const showTickets = user ? !location.pathname.includes("/eventSelect") : true

  const supportItem: TNavItem = {
    key: "support",
    label: "Suporte",
    to: "/suporte",
    active: isOnSupport,
    icon: <LifeBuoy aria-hidden="true" />,
  }
  const ticketsItems: TNavItem[] = showTickets
    ? [
        {
          key: "tickets",
          label: "Meus ingressos",
          to: user ? "/mytickets" : "/login",
          active: isOnMyTickets,
          icon: <Ticket aria-hidden="true" />,
        },
      ]
    : []

  // Computador: Suporte ao lado de Meus Ingressos. Celular: Meus ingressos primeiro.
  const items: TNavItem[] = [supportItem, ...ticketsItems]
  const mobileItems: TNavItem[] = [...ticketsItems, supportItem]

  // Fecha o menu ao trocar de página e com Esc.
  useEffect(() => {
    setMenuOpened(false)
  }, [location.pathname])

  useEffect(() => {
    if (!menuOpened) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpened(false)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [menuOpened])

  return (
    <S.Wrapper>
      <Container>
        <S.Component>
          {hideLogo ? (
            <span aria-hidden="true" />
          ) : (
            <S.LogoArea>
              <Link to={"/eventSelect"}>
                {(customData?.logoWebstore ?? event?.logoWebstore) ? (
                  <img
                    src={(customData?.logoWebstore ?? event?.logoWebstore)}
                    alt={(customData?.corporateName ?? event?.corporateName)}
                  />
                ) : (
                  <span>Início</span>
                )}
              </Link>
            </S.LogoArea>
          )}

          {/* Computador: links em linha. */}
          <S.DesktopNav aria-label="Menu da loja">
            {items.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={
                  item.key === "tickets" && !user
                    ? "nav-primary"
                    : item.active
                    ? "nav-primary"
                    : "nav-link"
                }
                aria-current={item.active ? "page" : undefined}
                onClick={(e) => goTo(e, item.to)}
              >
                {item.label === "Meus ingressos" ? "Meus Ingressos" : item.label}
              </Link>
            ))}

            {user && (
              <>
                <span className="nav-divider" aria-hidden="true" />
                <Link
                  to={"/"}
                  className="nav-logout"
                  onClick={handleLogout}
                  aria-label="Sair da conta"
                >
                  <LogOut aria-hidden="true" />
                  {"Sair"}
                </Link>
              </>
            )}
          </S.DesktopNav>

          {/* Celular: botão e cartão compacto abaixo dele. */}
          <S.MobileNav>
            <S.MenuButton
              type="button"
              aria-label={menuOpened ? "Fechar menu" : "Abrir menu"}
              aria-expanded={menuOpened}
              aria-controls="store-menu"
              onClick={() => setMenuOpened(!menuOpened)}
            >
              {menuOpened ? <X aria-hidden="true" /> : <BurguerIcon aria-hidden="true" />}
            </S.MenuButton>

            {menuOpened && (
              <>
                <S.Backdrop
                  data-testid="menu-backdrop"
                  onClick={() => setMenuOpened(false)}
                />
                <S.MenuPanel id="store-menu" role="menu" aria-label="Menu da loja">
                  {mobileItems.map((item) => (
                    <S.MenuItem
                      key={item.key}
                      to={item.to}
                      role="menuitem"
                      $active={item.active}
                      aria-current={item.active ? "page" : undefined}
                      onClick={(e) => goTo(e, item.to)}
                    >
                      {item.icon}
                      {item.label}
                    </S.MenuItem>
                  ))}

                  {user && (
                    <>
                      <S.MenuDivider aria-hidden="true" />
                      <S.MenuItem
                        to={"/"}
                        role="menuitem"
                        $active={false}
                        $muted
                        onClick={handleLogout}
                      >
                        <LogOut aria-hidden="true" />
                        Sair
                      </S.MenuItem>
                    </>
                  )}
                </S.MenuPanel>
              </>
            )}
          </S.MobileNav>
        </S.Component>
      </Container>
    </S.Wrapper>
  )
}

export default Header
